/**
 * Vorlage „Belegen“.
 *
 * Ein Boden liegt schräg von oben gesehen auf dem Blech. Bei der Pizza wird
 * zuerst die Soße verstrichen, dann fallen Salamischeiben einzeln auf ihren
 * Platz und zum Schluss rieselt Käse darüber. Beim Obstkuchen werden
 * Fruchthälften in Kreisen dicht an dicht gelegt.
 */
import { ease, lerp, random, segment } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { ellipsePath, path } from '../paths.ts';
import { compact, ellipse, faded, group, line, rect, shape, styles } from '../scene.ts';
import type { MaybeNode, SceneNode } from '../scene.ts';
import { falling } from '../parts/effects.ts';
import { arrow, counter, GROUND_Y, shadow } from '../parts/stage.ts';
import { place, woodenSpoon } from '../parts/utensils.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Boden in der Schrägansicht. */
const BASE = { cx: 112, cy: 110, rx: 88, ry: 20 } as const;

/** Beläge. */
const ITEMS = { pizza: 'Pizza', fruit: 'Obstkuchen' } as const;
type Item = keyof typeof ITEMS;

/** Zeitplan. */
const TIME = {
  sauce: [0.04, 0.22],
  toppings: [0.24, 0.66],
  cheese: [0.66, 0.84],
  fruit: [0.06, 0.78],
  reset: [0.88, 1],
} as const;

/** Lage auf dem Boden: u, v im Einheitskreis. */
function onBase(u: number, v: number, inset: number): Point {
  return { x: BASE.cx + u * (BASE.rx - inset), y: BASE.cy + v * (BASE.ry - inset * 0.3) };
}

/** Plätze der Salamischeiben. */
const SLICES: readonly (readonly [number, number])[] = [
  [-0.62, -0.25],
  [-0.2, -0.5],
  [0.25, -0.45],
  [0.62, -0.15],
  [-0.4, 0.35],
  [0.05, 0.05],
  [0.45, 0.4],
  [-0.05, 0.6],
  [-0.75, 0.2],
];

/** Plätze der Früchte: äußerer und innerer Kreis, von hinten nach vorn sortiert. */
const FRUITS: readonly (readonly [number, number])[] = [
  ...Array.from({ length: 11 }, (_, index): [number, number] => {
    const angle = (index / 11) * Math.PI * 2 + 0.3;
    return [Math.cos(angle) * 0.82, Math.sin(angle) * 0.72];
  }),
  ...Array.from({ length: 6 }, (_, index): [number, number] => {
    const angle = (index / 6) * Math.PI * 2;
    return [Math.cos(angle) * 0.42, Math.sin(angle) * 0.34];
  }),
  [0, 0],
];

/** Salamischeibe. */
function salami(at: Point): SceneNode[] {
  return [
    ellipse(at.x, at.y, 12, 4.6, styles.solid('shade')),
    ellipse(at.x - 4, at.y - 0.8, 1.8, 1, styles.flat('paper')),
    ellipse(at.x + 3.5, at.y + 1, 1.6, 0.9, styles.flat('paper')),
    ellipse(at.x + 1, at.y - 2, 1.2, 0.7, styles.flat('paper')),
  ];
}

/** Halbe Pflaume mit der Schnittfläche nach oben. */
function fruitHalf(at: Point): SceneNode[] {
  return [
    ellipse(at.x, at.y, 10, 4.4, styles.solid('deep')),
    ellipse(at.x, at.y - 0.5, 6.5, 2.4, styles.flat('shade')),
    line(at.x - 3, at.y - 0.5, at.x + 3, at.y - 0.5, styles.fine('deep')),
  ];
}

/** Ein Belag, der von oben auf seinen Platz fällt. */
function drop(at: Point, progress: number, draw: (point: Point) => SceneNode[]): MaybeNode {
  if (progress <= 0) {
    return null;
  }
  const y = lerp(at.y - 28, at.y, ease.inQuad(progress));
  return faded(Math.min(1, progress * 3), draw({ x: at.x, y }));
}

/** Blendet nur ein, wenn es etwas zu zeigen gibt; leere Gruppen stören die nahtlose Schleife. */
function fadedSome(opacity: number, children: readonly MaybeNode[]): MaybeNode {
  return children.some((child) => child !== null && child !== undefined && child !== false)
    ? faded(opacity, children)
    : null;
}

/** Boden mit Rand; thickness ist die Höhe der Kante. */
function base(thickness: number, topTone: 'tint' | 'shade'): SceneNode[] {
  const { cx, cy, rx, ry } = BASE;
  const side = path()
    .move(cx - rx, cy)
    .line(cx - rx, cy + thickness)
    .arc(rx, ry, false, false, cx + rx, cy + thickness)
    .line(cx + rx, cy)
    .arc(rx, ry, false, true, cx - rx, cy)
    .close()
    .toString();
  return [shape(side, styles.solid('shade')), shape(ellipsePath(cx, cy, rx, ry), styles.solid(topTone))];
}

/** Vorlage „Belegen“. */
export const bakeTopTemplate = defineTemplate({
  id: 'bake.top',
  group: 'bake',
  title: 'Belegen',
  description:
    'Der Boden wird gleichmäßig belegt: Bei der Pizza zuerst Soße, dann der Belag und zum Schluss Käse, beim Obstkuchen Früchte dicht an dicht in Kreisen.',
  durationSeconds: 7,
  keywords: [
    'belegen',
    'belegt',
    'pizza',
    'obstkuchen',
    'mit obst',
    'darauf verteilen',
    'darauf legen',
    'auflegen',
    'dachziegelartig',
    'mit käse bestreuen',
    'tomatensoße',
    'tomatensauce',
  ],
  stillTime: 0.62,
  parameters: [choice('item', 'Boden', ITEMS, 'pizza')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/pizza|salami|flammkuchen|tomatenso/.test(lower)) return { item: 'pizza' };
    if (/obst|früchte|pflaum|zwetschg|äpfel|apfel|kirsch|beeren|aprikos/.test(lower))
      return { item: 'fruit' };
    return {};
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'pizza') as Item;
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const visible = 1 - reset;
    const nodes: MaybeNode[] = [
      ...counter(),
      shadow(BASE.cx, BASE.rx * 0.95, GROUND_Y + 1, 4),
      // Blech
      rect(BASE.cx - BASE.rx - 12, GROUND_Y - 6, BASE.rx * 2 + 24, 6, 2, styles.solid('ink')),
    ];

    if (item === 'pizza') {
      nodes.push(...base(7, 'tint'));
      // Soße wächst von der Mitte aus, ein Löffel verstreicht sie.
      const spread = ease.outCubic(segment(t, ...TIME.sauce));
      const sauceRx = (BASE.rx - 12) * spread;
      const sauce =
        spread > 0.01
          ? faded(visible, [
              ellipse(BASE.cx, BASE.cy, sauceRx, (BASE.ry - 4) * spread, {
                fill: 'deep',
                stroke: 'ink',
                strokeWidth: STROKE.detail,
              }),
            ])
          : null;
      const spoonPhase = segment(t, ...TIME.sauce);
      const spoonVisible =
        Math.min(1, spoonPhase * 8) * (1 - segment(t, TIME.sauce[1] - 0.03, TIME.sauce[1]));
      const spoonAngle = spoonPhase * Math.PI * 4;
      const spoon = faded(spoonVisible, [
        place(
          BASE.cx + Math.cos(spoonAngle) * sauceRx * 0.7,
          BASE.cy + Math.sin(spoonAngle) * (BASE.ry - 4) * spread * 0.7,
          20,
          woodenSpoon(),
          0.8,
        ),
      ]);
      // Salami fällt Scheibe für Scheibe.
      const count = SLICES.length;
      const toppings = SLICES.map(([u, v], index) => {
        const start = lerp(TIME.toppings[0], TIME.toppings[1] - 0.06, index / (count - 1));
        return { at: onBase(u, v, 18), progress: segment(t, start, start + 0.06) };
      })
        .sort((a, b) => a.at.y - b.at.y)
        .map((entry) => drop(entry.at, entry.progress, salami));
      // Käse: fallende Flocken, dann kurze Streifen auf dem Belag.
      const cheesePhase = segment(t, ...TIME.cheese);
      const rng = random(31);
      const shreds: SceneNode[] = [];
      for (let index = 0; index < 22; index += 1) {
        const point = onBase(rng() * 2 - 1, rng() * 2 - 1, 20);
        const angle = (rng() - 0.5) * 70;
        const appear = rng() * 0.8;
        if (cheesePhase > appear) {
          const dx = Math.cos((angle * Math.PI) / 180) * 3.5;
          const dy = Math.sin((angle * Math.PI) / 180) * 1.2;
          shreds.push(
            line(point.x - dx, point.y - dy, point.x + dx, point.y + dy, {
              stroke: 'snow',
              strokeWidth: STROKE.detail,
            }),
          );
        }
      }
      const flakes =
        cheesePhase > 0 && cheesePhase < 1
          ? falling(BASE.cx, 20, BASE.cy, 120, 18, t, { seed: 8, size: 1.6, loops: 4, tone: 'snow' })
          : [];
      nodes.push(
        sauce,
        fadedSome(visible, toppings),
        shreds.length > 0
          ? faded(visible, [group(shreds, { clip: ellipsePath(BASE.cx, BASE.cy, BASE.rx - 8, BASE.ry - 2) })])
          : null,
        ...flakes,
        spoon,
      );
    } else {
      nodes.push(...base(12, 'tint'));
      // Innerer Rand des Mürbeteigs.
      nodes.push(
        shape(ellipsePath(BASE.cx, BASE.cy + 1, BASE.rx - 7, BASE.ry - 3), {
          fill: 'none',
          stroke: 'shade',
          strokeWidth: STROKE.detail,
        }),
      );
      const count = FRUITS.length;
      const fruits = FRUITS.map(([u, v], index) => {
        const start = lerp(TIME.fruit[0], TIME.fruit[1] - 0.05, index / (count - 1));
        return { at: onBase(u, v, 14), progress: segment(t, start, start + 0.05) };
      })
        .sort((a, b) => a.at.y - b.at.y)
        .map((entry) => drop(entry.at, entry.progress, fruitHalf));
      nodes.push(fadedSome(visible, fruits));
    }
    return compact(nodes);
  },
  hints() {
    return [
      arrow([
        { x: BASE.cx + 60, y: 22 },
        { x: BASE.cx + 40, y: 56 },
        { x: BASE.cx + 30, y: BASE.cy - 18 },
      ]),
    ];
  },
});
